/**
 * สไลด์ in the bout (duel v3 P1.5, data/slide.ts `DUEL_SLIDE`; research docs/research/DUEL_SLIDE.md). Bout only (`DUEL_FLAGS.v2`,
 * a fight under way); the open world keeps `SLIDE` and systems/slide.ts as they are.
 *
 * The step (systems/duelMove.ts) is the short i-frame dodge; the slide is the long ground move, with its own role:
 * - Reposition / gap-close / escape along the ground: 4.5 m in 0.5 s (a step covers 2.2-4). Toward the foe it stops short of
 *   them (no sliding through). No i-frames: a ground blow hits a slider.
 * - Low: for its first `low` share, a blow from a fighter in the air (the dive, an air move) passes over it. The answer to a
 *   read jump-in, as a low slide goes under a jump in fighting games.
 * - Punishable: one that runs out (nobody cuts it short) ends in a `stop` longer than any step's (duelRecovery.startStop). It
 *   cannot start inside a stop or the escape's step lock, and a second one inside `spamWindow` costs more.
 * - Slide strike: the basic cut or an attack move started in the end window (a move pressed earlier waits for the window)
 *   cuts the slide with no stop and hits harder; guarded, it leaves the striker in a stop.
 * Deterministic: time only from the hooks' dt; no randomness here.
 */
import { DUEL_SLIDE } from '../data/slide';
import type { HitResult } from '../combat';
import type { SkillKind } from '../data/skills';
import type { Entity, PlayerCommand, WorldEvent } from '../world';
import { duelAirOf } from './duelAir';
import { duelMoveOf } from './duelMove';
import { duelRecoveryOf } from './duelRecovery';
import { setSlideTuner, slideOf } from './slide';
import type { SystemHost, WorldSystem } from './types';

const SYSTEMS = new WeakMap<object, DuelSlideSystem>();
export const duelSlideOf = (w: object): DuelSlideSystem | null => SYSTEMS.get(w) ?? null;

/** How a bout slide ended (for the picture and the tests). */
export type SlideEnd = 'ran-out' | 'strike' | 'jump' | 'taken';

export class DuelSlideSystem implements WorldSystem {
  readonly name = 'duelSlide';
  /** World time of each fighter's latest bout slide. */
  private readonly last = new Map<number, number>();
  /** Fighters sliding at the end of the last tick. */
  private readonly sliding = new Set<number>();
  /** A move pressed before the end window, fired when it opens. */
  private readonly held = new Map<number, PlayerCommand>();
  /** A slide strike's blow is due until this world time. */
  private readonly strikeUntil = new Map<number, number>();
  /** Fighters in the air (or diving) at the end of the last tick: their blows pass over a low slide. */
  private readonly air = new Set<number>();
  /** The latest end of each fighter's slide. */
  private readonly ends = new Map<number, { how: SlideEnd; at: number }>();

  constructor(private readonly h: SystemHost) {
    SYSTEMS.set(h.world, this);
  }

  /** How and when a fighter's latest bout slide ended (null: none yet). */
  lastEnd(id: number): { how: SlideEnd; at: number } | null {
    return this.ends.get(id) ?? null;
  }

  /** True while a slide strike's blow is due (the renderer may mark it). */
  striking(id: number): boolean {
    return (this.strikeUntil.get(id) ?? -1) > this.h.world.time;
  }

  private inBout(id: number): boolean {
    return !!duelMoveOf(this.h.world)?.inBout(id);
  }

  setup(): void {
    const w = this.h.world;
    setSlideTuner(w, (p, dx, dy) => this.tune(p, dx, dy));
    // The bout's hit rules (systems/duelMatch.ts sets them up first): the slide's own rules go before them.
    const prev = w.duelMod;
    w.duelMod = (src, tgt, res, kind, skillId) => {
      this.mod(src, tgt, res, kind);
      prev?.(src, tgt, res, kind, skillId);
      if (res.outcome === 'blocked' && this.strikeUntil.has(src.id)) {
        this.strikeUntil.delete(src.id);
        duelRecoveryOf(w)?.startStop(src.id, DUEL_SLIDE.strike.guardedStop, 'slide-strike-guarded');
      } else if (res.outcome !== 'dodge') this.strikeUntil.delete(src.id);
    };
  }

  /** A bout slide's numbers: longer and quicker; toward the foe it stops short of them; a second in a row costs more. */
  private tune(p: Entity, dx: number, dy: number): { dist: number; time: number; stamina: number; cancelAt: number } | null {
    if (!this.inBout(p.id)) return null;
    const w = this.h.world;
    let dist: number = DUEL_SLIDE.dist;
    const foe = duelMoveOf(w)?.foeOf(p.id);
    if (foe) {
      const rx = foe.x - p.x;
      const ry = foe.y - p.y;
      const along = rx * dx + ry * dy;
      const across = Math.abs(rx * dy - ry * dx);
      if (along > 0 && across < DUEL_SLIDE.lane) dist = Math.max(0.3, Math.min(dist, along - DUEL_SLIDE.short));
    }
    const lastAt = this.last.get(p.id);
    const spam = lastAt !== undefined && w.time - lastAt < DUEL_SLIDE.spamWindow;
    return {
      dist,
      // A shortened slide is quicker too (the same friction curve over a shorter way).
      time: DUEL_SLIDE.time * Math.max(0.5, dist / DUEL_SLIDE.dist),
      stamina: DUEL_SLIDE.stamina * (spam ? DUEL_SLIDE.spamMult : 1),
      cancelAt: DUEL_SLIDE.cancelAt,
    };
  }

  refuse(cmd: PlayerCommand): boolean {
    const w = this.h.world;
    const p = w.player;
    if (!this.inBout(p.id)) return false;
    if (cmd.t === 'slide') {
      // Not out of a stop, nor inside the escape's step lock.
      if ((duelRecoveryOf(w)?.left(p.id) ?? 0) > 0) return true;
      if ((duelMoveOf(w)?.lockLeft(p.id) ?? 0) > 0) return true;
      return false;
    }
    // A move pressed before the end window waits for it (the slide strike).
    if (cmd.t === 'skill' || cmd.t === 'attack') {
      const s = slideOf(p);
      if (s && s.t < s.cancelAt * s.time) {
        this.held.set(p.id, cmd);
        return true;
      }
    }
    return false;
  }

  observe(e: WorldEvent): void {
    if (e.type === 'slide' && this.inBout(e.id)) {
      this.last.set(e.id, this.h.world.time);
      this.sliding.add(e.id);
    }
  }

  /** The slide's low profile and the slide strike's weight, before the bout's own rules. */
  private mod(src: Entity, tgt: Entity, res: HitResult, kind: SkillKind): void {
    const s = slideOf(tgt);
    if (s && s.t < DUEL_SLIDE.low * s.time && (src.z > 0 || this.air.has(src.id)) && res.outcome !== 'dodge') {
      Object.assign(res, { outcome: 'dodge', damage: 0, staminaDamage: 0, stun: 0, crit: false });
      return;
    }
    if (kind === 'attack' && this.striking(src.id) && res.damage > 0)
      res.damage = Math.max(1, Math.round(res.damage * DUEL_SLIDE.strike.power));
  }

  step(_dt: number): void {
    const w = this.h.world;
    const b = w.pvpBout;
    const ids = b && b.fight ? [b.a, b.b] : [];
    for (const id of [...this.sliding, ...this.held.keys()]) if (!ids.includes(id)) this.forget(id);
    this.air.clear();
    for (const id of ids) {
      const e = w.get(id);
      if (!e) continue;
      if (e.z > 0 || duelAirOf(w)?.dive(id)) this.air.add(id);
      const s = slideOf(e);
      // The waiting move, once the end window is open.
      const cmd = this.held.get(id);
      if (cmd && (!s || s.t >= s.cancelAt * s.time)) {
        this.held.delete(id);
        if (s) {
          this.strikeUntil.set(id, w.time + DUEL_SLIDE.strike.hold);
          w.apply(cmd, id);
        }
      }
      if (s || !this.sliding.has(id)) continue;
      // The slide ended this tick: how?
      this.sliding.delete(id);
      let how: SlideEnd;
      if (e.swingT > 0 || e.cast) {
        how = 'strike';
        if (!this.striking(id)) this.strikeUntil.set(id, w.time + DUEL_SLIDE.strike.hold);
      } else if (e.z > 0 || e.vz > 0) how = 'jump';
      else if (e.stunT > 0 || e.dashT > 0 || e.dead) how = 'taken';
      else {
        how = 'ran-out';
        duelRecoveryOf(w)?.startStop(id, DUEL_SLIDE.stop, 'slide');
      }
      this.ends.set(id, { how, at: w.time });
    }
  }

  private forget(id: number): void {
    this.sliding.delete(id);
    this.held.delete(id);
    this.strikeUntil.delete(id);
  }
}
